import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Overline } from "../components/Primitives";
import { submitApplication } from "../lib/api";
import { Seo } from "../components/Seo";

const Field = ({ label, name, value, onChange, type = "text", required, testid, textarea, placeholder }) => (
  <div className="flex flex-col">
    <label htmlFor={name} className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-kenosis-gold/80">
      {label}{required && <span className="text-kenosis-gold"> *</span>}
    </label>
    {textarea ? (
      <textarea
        id={name} name={name} value={value} onChange={onChange} rows={3} data-testid={testid} placeholder={placeholder}
        className="mt-3 resize-none border-b border-white/20 bg-transparent py-3 font-sans text-base font-light text-kenosis-ivory placeholder:text-kenosis-muted/40 transition-colors focus:border-kenosis-gold focus:outline-none"
      />
    ) : (
      <input
        id={name} name={name} type={type} value={value} onChange={onChange} required={required} data-testid={testid} placeholder={placeholder}
        className="mt-3 border-b border-white/20 bg-transparent py-3 font-sans text-base font-light text-kenosis-ivory placeholder:text-kenosis-muted/40 transition-colors focus:border-kenosis-gold focus:outline-none"
      />
    )}
  </div>
);

export default function Apply() {
  const { t, lang } = useLang();
  const f = t("apply.form");
  const process = t("apply.process");
  const formatOptions = f.formatOptions;

  const [data, setData] = useState({
    name: "", email: "", country: "", profession: "", mainChallenge: "",
    goals: "", preferredFormat: "", availability: "", workedWithProfessionals: "",
    whyKenosis: "", medicalNotes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.country || !data.mainChallenge) {
      toast.error(f.required);
      return;
    }
    setBusy(true);
    try {
      await submitApplication({ ...data, language: lang });
      setDone(true);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="apply-page">
      <Seo pageKey="apply" path="/apply" />
      <section className="relative pt-40 md:pt-52 pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal><Overline>{t("apply.heroOverline")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[1.02] tracking-tight text-kenosis-ivory">{t("apply.title")}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-ivory/70">{t("apply.intro")}</p>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-8 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {process.map((p, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-kenosis-black p-8">
              <span className="font-serif text-4xl text-kenosis-gold/40">{p.num}</span>
              <h3 className="mt-4 font-serif text-2xl font-normal text-kenosis-ivory">{p.title}</h3>
              <p className="mt-2 font-sans text-sm font-light text-kenosis-muted">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Form / Success */}
      <section className="relative py-20 md:py-28">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success" data-testid="apply-success"
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="border border-kenosis-gold/30 bg-kenosis-surface p-10 md:p-16 text-center"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-kenosis-gold/50">
                  <Check className="text-kenosis-gold" size={28} />
                </div>
                <h2 className="mt-8 font-serif text-3xl md:text-4xl font-light text-kenosis-ivory">{t("apply.success.title")}</h2>
                <p className="mx-auto mt-4 max-w-lg font-sans text-base font-light leading-relaxed text-kenosis-muted">{t("apply.success.text")}</p>
              </motion.div>
            ) : (
              <motion.form
                key="form" onSubmit={onSubmit} data-testid="apply-form"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10"
              >
                <Field label={f.name} name="name" value={data.name} onChange={onChange} required testid="field-name" />
                <Field label={f.email} name="email" type="email" value={data.email} onChange={onChange} required testid="field-email" />
                <Field label={f.country} name="country" value={data.country} onChange={onChange} required testid="field-country" />
                <Field label={f.profession} name="profession" value={data.profession} onChange={onChange} testid="field-profession" />

                <div className="md:col-span-2">
                  <Field label={f.challenge} name="mainChallenge" value={data.mainChallenge} onChange={onChange} required textarea testid="field-challenge" />
                </div>
                <div className="md:col-span-2">
                  <Field label={f.goals} name="goals" value={data.goals} onChange={onChange} textarea testid="field-goals" />
                </div>

                <div className="flex flex-col">
                  <label className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-kenosis-gold/80">{f.format}</label>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {formatOptions.map((opt) => (
                      <button
                        type="button" key={opt} data-testid={`format-${opt}`}
                        onClick={() => setData((d) => ({ ...d, preferredFormat: opt }))}
                        className={`border px-5 py-3 font-sans text-xs uppercase tracking-[0.15em] transition-colors ${
                          data.preferredFormat === opt ? "border-kenosis-gold bg-kenosis-gold/10 text-kenosis-gold" : "border-white/20 text-kenosis-ivory/70 hover:border-white/40"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label={f.availability} name="availability" value={data.availability} onChange={onChange} testid="field-availability" />

                <div className="md:col-span-2">
                  <Field label={f.worked} name="workedWithProfessionals" value={data.workedWithProfessionals} onChange={onChange} textarea testid="field-worked" />
                </div>
                <div className="md:col-span-2">
                  <Field label={f.why} name="whyKenosis" value={data.whyKenosis} onChange={onChange} textarea testid="field-why" />
                </div>
                <div className="md:col-span-2">
                  <Field label={f.medical} name="medicalNotes" value={data.medicalNotes} onChange={onChange} textarea testid="field-medical" />
                </div>

                <div className="md:col-span-2 mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-8">
                  <button
                    type="submit" disabled={busy} data-testid="apply-submit"
                    className="group relative overflow-hidden border border-kenosis-gold/60 px-10 py-4 disabled:opacity-50"
                  >
                    <span className="relative z-10 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-gold transition-colors duration-500 group-hover:text-kenosis-black">
                      {busy ? f.submitting : f.submit}
                    </span>
                    <span className="absolute inset-0 z-0 origin-left scale-x-0 bg-kenosis-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
                  </button>
                  <p className="max-w-xs font-sans text-xs font-light leading-relaxed text-kenosis-muted">{t("common.responseTime")}</p>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Geographic note */}
      <section className="relative border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <p className="font-serif text-2xl md:text-3xl font-light italic text-kenosis-ivory/90">{t("common.europeBased")}</p>
          <p className="self-center font-sans text-sm font-light leading-relaxed text-kenosis-muted">{t("common.usExcluded")}</p>
        </div>
      </section>
    </div>
  );
}
